import { Link } from "gatsby"
import React, { useContext } from "react"
import Layout from "../../components/layout"
import Seo from "../../components/seo"
import { StaticImage } from "gatsby-plugin-image"

import styled from "styled-components"

import WebriQForm from "@webriq/gatsby-webriq-form"

const Banner = () => {
  return (
    <>
      <div className="hero-new-study">
        <div className="container">
          <div className="row">
            <div className="col-md-12 mx-auto">
              <div className="title-banner">
                <h1 className="text-center">
                  From Planning Chaos to Control: How a Global Tech Firm
                  Unblocked 2,500 Critical Construction Projects
                </h1>
                <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-sm-start text-center text-sm-left text-white">
                  <p className="pr-sm-1 mb-1 mb-sm-0">
                    <strong>Summary:</strong>
                  </p>
                  <p>
                    A bloated, spreadsheet-based planning system left a global
                    tech company unable to control priorities, forecast project
                    starts, or manage 2,500 construction initiatives. Quandary
                    built a Quickbase application that gave 800 users real-time
                    visibility and control.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 24px;
  padding: 24px 0;
`

const LogoCard = styled.div`
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  min-width: 160px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 12px;
`

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 48px;
  object-fit: contain;
`

const Custom = ({ location }) => {
  return (
    <>
      <Layout banner={<Banner />} location={location}>
        <Seo
          title="Tech Giant Cuts Planning Delays with Custom Tool"
          description="How Quandary helped a global tech firm replace spreadsheets with a custom tool, giving 800 users control over 2,500 projects and slashing a 12-month backlog."
        />
        <div className="py-4 py-md-5 case-studies-inner new-case-studies-inner bg-white">
          <div className="container">
            <div className="row">
              <div className="col-md-4 col-lg-4">
                <div
                  className="sidebar-tab relative results"
                  style={{ position: "sticky", top: "110px" }}
                >
                  <div className="relative results bg-case-study p-4">
                    <h3 className="mt-0 ">Results Snapshot</h3>
                    <ul className="ml-0">
                      <li>9-12 month ticket time reduced to minutes</li>
                      <li>0 escalations during portfolio planning</li>
                      <li>Planning updates delivered in under 1 day</li>
                      <li>800+ users with controlled access</li>
                      <li>2,500+ projects tracked in one system</li>
                    </ul>
                  </div>
                  <Link
                    className="btn btn-primary btn-arrow mr-2 mt-4 text-center relative w-full"
                    to="https://cdn.sanity.io/files/55lcecww/production/e43d3ffcab085d101b6be15e6133be0cc4f6ca62.pdf"
                  >
                    Download This Story
                  </Link>
                </div>
              </div>
              <div className="col-md-8 col-lg-8">
                <section className="relative">
                  <div className="relative px-0">
                    <div className="row mx-0">
                      <div className="col-md-11 mx-auto p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Challenge</h2>
                          <p>
                            The client, a global tech company managing thousands
                            of data center construction projects, was near a
                            breaking point.
                          </p>
                          <p>
                            Their planning process relied on spreadsheets and
                            internal tools that could not keep up with the
                            scale, complexity, or urgency of the work.
                          </p>
                          <p>
                            With over 2,500 projects and more than 800 users,
                            there was no way to track readiness or priority,
                            control who changed critical project data, or even
                            forecast whether a project would launch in the
                            coming year.
                          </p>
                          <p>
                            As a result, budgets were spent on low-priority
                            work, high-impact projects stalled, and planning
                            teams lacked the visibility to course-correct in
                            time.
                          </p>
                          <p>
                            <strong>Internally, frustration mounted:</strong>
                          </p>
                          <ul className="pl-4 pb-3">
                            <li>
                              No real-time reporting across the project
                              portfolio
                            </li>

                            <li>
                              No controls over access, edits, or approvals
                            </li>
                            <li>
                              No way to identify which projects should move
                              forward or wait
                            </li>
                          </ul>
                          <p>
                            The planning group knew they could not build for
                            another year using disconnected spreadsheets. They
                            needed a better way to prioritize development.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Our Solution</h2>
                          <p>
                            The client needed a better way to manage thousands
                            of overlapping projects without losing control over
                            who could update, approve, or prioritize critical
                            planning data.
                          </p>
                          <p>
                            We built{" "}
                            <Link to="/custom-quickbase-application-development/">
                              a custom application in Quickbase
                            </Link>{" "}
                            that aligned with their internal planning standards
                            and supported the complexity of global construction
                            projects.
                          </p>
                          <p>
                            <strong>The tool allowed the team to:</strong>
                          </p>
                          <ul className="pl-4">
                            <li>
                              Configure access rules to control who could edit
                              and approve projects
                            </li>
                            <li>
                              Standardize project evaluation using five internal
                              planning criteria
                            </li>
                            <li>
                              <Link to="/quickbase-integration-services/">
                                Integrate with their intake system
                              </Link>{" "}
                              while still functioning independently
                            </li>
                          </ul>
                          <p className="pt-3">
                            By anchoring the system in the client’s own
                            decision-making framework, we ensured it could scale
                            across regions and teams without introducing
                            bottlenecks or workarounds.
                          </p>
                          <p>
                            <strong>
                              Specifically, it guides the team to address these
                              questions:
                            </strong>
                          </p>
                          <ul className="pl-4">
                            <li>
                              Configure access rules to control who could edit
                              and approve projects
                            </li>
                            <li>Can we select it?</li>
                            <li>Can we support it?</li>
                            <li>Can we complete it within the timeframe?</li>
                            <li>Is it within budget for our objectives?</li>
                            <li>Is it within budget for our region?</li>
                          </ul>

                          <p className="pt-3">
                            <strong>Key Capabilities</strong>
                          </p>
                          <p>
                            Each feature was designed to help the client
                            prioritize projects more effectively, enforce
                            planning discipline, and reduce misalignment across
                            global teams.
                          </p>
                          <ul className="pl-4">
                            <li>
                              <strong>
                                Prioritization by Readiness and Budget Fit:{" "}
                              </strong>{" "}
                              Teams can now rank projects based on internal
                              readiness tiers (R1–R5), strategic priority
                              (P0–P4), and available cash flow—enabling faster,
                              clearer decision-making.
                            </li>
                            <li>
                              <strong>Structured Approval Paths:</strong> Only
                              authorized users can submit or approve projects,
                              with clear role-based access that locks once
                              planning deadlines pass. This reduces conflicting
                              edits and last-minute changes.
                            </li>
                            <li>
                              <strong>Global Planning Filters:</strong> Teams
                              can evaluate projects by objective, region, or
                              owner, ensuring that local efforts align with
                              global goals.
                            </li>
                            <li>
                              <strong>
                                Connected, Flexible Architecture:{" "}
                              </strong>{" "}
                              The tool can also run independently of the intake
                              system, preserving autonomy without sacrificing
                              cross-system visibility.
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>Our Process</h2>
                          <p>
                            <Link to="/our-approach">
                              We started with a discovery sprint
                            </Link>{" "}
                            to untangle the client’s existing planning
                            workflows, data structures, and system limitations.
                          </p>
                          <p>
                            From there, <Link to="/faq">we moved fast</Link> to
                            design and deliver a tool that could handle global
                            planning demands without locking teams into rigid
                            workflows.
                          </p>
                          <p>
                            <strong>Key Steps:</strong>
                          </p>
                          <ol className="decimal">
                            <li>
                              Audited the client’s spreadsheet-based system to
                              identify blockers and manual workarounds
                            </li>
                            <li>
                              Mapped their internal planning standards into a
                              digital workflow, enforcing clear roles and
                              approval paths
                            </li>
                            <li>
                              Built the new tool in Quickbase with dual
                              capability (integrated with their intake system
                              but able to run independently)
                            </li>
                            <li>
                              Delivered iterative builds for daily review,
                              incorporating team feedback in real time
                            </li>
                            <li>
                              Aligned permissions and approval logic to reduce
                              risk and prevent conflicting edits
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-gradient">
                        <div className="client-content">
                          <h2 className="text-white">The Roadblocks</h2>
                          <p className="text-white">
                            Delivering this tool meant navigating both technical
                            and cultural friction.
                          </p>
                          <p className="text-white">
                            The client’s teams were used to waterfall-style
                            development and long approval cycles. Shifting to
                            our agile approach, which relied on rapid iteration
                            and daily feedback, was unfamiliar at first. But
                            once they saw how quickly their input shaped the
                            product, the pace became a strength.
                          </p>
                          <p className="text-white">
                            The complexity of the planning environment meant no
                            two teams used the system the same way. Regular
                            feedback loops helped ensure the tool fit the
                            real-world behaviors of every stakeholder group
                            before full deployment.
                          </p>
                          <p className="text-white">
                            Internally, every stakeholder group had its own
                            workflow and expectations for how planning should
                            work. Some teams prioritized speed. Others
                            prioritized budget. Some focused on global strategy,
                            while others handled local execution. Aligning these
                            competing needs required constant recalibration and
                            compromise.
                          </p>
                          <p className="text-white">
                            Finally, adoption required a mindset shift. Teams
                            had to rethink how they submitted, approved, and
                            prioritized work. What was once flexible and
                            informal now followed clear rules. That structure
                            created early resistance, but it also made the wins
                            visible.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>The Toolstack</h2>
                          <LogoGrid>
                            <LogoCard>
                              <LogoImg
                                src="https://cdn.sanity.io/images/55lcecww/production/78673169bfad856d32e060c5bf029890db184c60-261x101.png"
                                alt="Quickbase"
                              />
                            </LogoCard>
                          </LogoGrid>
                          <p>
                            The client’s planning tool was built entirely in
                            Quickbase, chosen for its flexibility and ability to
                            support complex workflows without heavy overhead.
                          </p>
                          <p>
                            <strong>Quickbase allowed us to:</strong>
                          </p>
                          <ul className="pl-4">
                            <li>
                              Configure access controls tied to planning
                              deadlines
                            </li>
                            <li>
                              Map internal approval logic without hardcoding
                              dependencies
                            </li>
                            <li>
                              Rapidly deliver changes based on daily feedback
                            </li>
                            <li>
                              Integrate with their intake system while keeping
                              the tool modular
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-blue">
                        <div className="client-content">
                          <h2 className="text-white">The Results</h2>
                          <p className="text-white">
                            The planning team now has full visibility into
                            2,500+ active and upcoming construction projects and
                            can make real-time decisions about what to
                            prioritize, delay, or decline.
                          </p>
                          <p className="text-white">
                            Previously, it took up to 12 months to make changes
                            to their planning tools. With the new Quickbase
                            system, teams can deploy updates in days (or
                            minutes) based on changing priorities.
                          </p>
                          <p className="text-white">
                            For the first time in the company’s history, the
                            annual planning cycle went forward without a single
                            escalation. Teams had what they needed, and
                            leadership had confidence in the process.
                          </p>
                          <p className="text-white">
                            What used to be a fragmented, spreadsheet-based
                            system is now a single source of truth. Every team
                            can see what matters, what is approved, and where
                            the budget is going.
                          </p>
                          <p>
                            <strong className="text-white">Key Results:</strong>
                          </p>
                          <ol className="decimal text-white">
                            <li className="text-white">
                              System update cycle reduced from 9-12 months to
                              days
                            </li>
                            <li className="text-white">
                              Eliminated escalations during global portfolio
                              planning
                            </li>
                            <li className="text-white">
                              Enabled real-time project visibility across 2,500+
                              initiatives
                            </li>
                            <li className="text-white">
                              Gave 800+ users access to a single, controlled
                              planning system
                            </li>
                            <li className="text-white">
                              Prioritized high-impact construction with clear
                              readiness and budget filters
                            </li>
                          </ol>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper">
                        <div className="client-content">
                          <h2>What’s Next?</h2>
                          <p>
                            The planning team is now focused on strengthening
                            the tool’s budgeting capabilities. They aim to
                            allocate funds more effectively to high-priority,
                            ready-to-go projects, avoiding slowdowns and
                            reducing wasted spend.
                          </p>
                          <p>
                            With the new system in place, they are better
                            equipped to refine planning cycles, manage
                            tradeoffs, and scale smarter year over year.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto p-4 p-4 content-wrapper bg-grey">
                        <div className="client-content">
                          <h2>Client Overview:</h2>
                          <p>
                            A U.S.-based global technology company with
                            operations spanning search, cloud infrastructure,
                            enterprise systems, and large-scale data center
                            engineering.
                          </p>
                          <p>
                            The client’s project planning team manages
                            large-scale construction programs for global data
                            centers. Our work focused on their intake and
                            portfolio management overhaul.
                          </p>
                          <div className="row align-items-center">
                            <div className="col-md-6 pr-md-4">
                              <p>
                                <strong>Industry:</strong> Technology
                                <span className="relative d-block">
                                  <strong>Location:</strong> Global
                                </span>
                                <span className="relative d-block">
                                  <strong>Size:</strong> 100,000+ employees
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-11 mx-auto px-0 pt-4 content-wrapper">
                        <div className="client-content">
                          <div id="speak-with-specialist">
                            <div className="form-wrap-contact">
                              <div className="form-wrapper">
                                <div className="intro mb-2">
                                  <div className="row">
                                    <div className="col-md-12">
                                      <h2 className="mt-0 font-weight-medium">
                                        Speak with Our Innovation Team
                                      </h2>
                                      <p>
                                        Use the form below to drop us an e-mail.
                                        We'll be in touch shortly.
                                      </p>
                                      <small className="text-red">
                                        Fields marked with an asterisk (*) are
                                        required.
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <WebriQForm
                                  id="contact-form"
                                  name="Contact Form"
                                  className="contactForm"
                                  data-form-id="4d46fe65-4d66-46b4-946c-e5826349ed3c"
                                  data-thankyou-url="/thank-you"
                                >
                                  <div className="row">
                                    <div className="col-lg-6 col-md-12 col-sm-12">
                                      {/* <p class="required">Full Name</p> */}

                                      <input
                                        type="text"
                                        name="Full Name"
                                        size={40}
                                        className="form-control required"
                                        placeholder="Full Name *"
                                        aria-label="Full Name"
                                        required
                                        id="requiredField"
                                      />
                                    </div>
                                    <div className="col-lg-6 col-md-12 col-sm-12 mt-4 mt-lg-0">
                                      {/* <p class="required">Phone</p> */}
                                      <input
                                        type="text"
                                        name="Phone"
                                        size={40}
                                        // minLength={14}
                                        className="form-control required"
                                        id="phone"
                                        placeholder="Phone Number *"
                                        aria-label="Phone"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Email</p> */}
                                      <input
                                        type="email"
                                        name="Email"
                                        size={40}
                                        className="form-control"
                                        aria-label="Email"
                                        placeholder="Email *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">Message</p> */}
                                      <textarea
                                        name="Message"
                                        rows={5}
                                        cols={0}
                                        className="form-control"
                                        aria-label="Message"
                                        placeholder="Message *"
                                        required
                                      />
                                    </div>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mt-4">
                                      {/* <p class="required">How did you find out about us?</p> */}
                                      <div>
                                        <textarea
                                          type="text"
                                          name="How did you find out about us?"
                                          size={40}
                                          rows={5}
                                          className="form-control"
                                          aria-label="How did you find out about us?"
                                          placeholder="How did you find out about us? *"
                                          required
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="form-group col-12 d-flex justify-content-start captcha-wrap">
                                      <div className="webriq-recaptcha" />
                                    </div>
                                    <div className="form-group col-12 text-left">
                                      <div className="input-filled">
                                        <button
                                          type="submit"
                                          className="btn btn-primary btn-arrow"
                                        >
                                          SUBMIT
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </WebriQForm>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}

export default Custom
